import React from "react";
import { ImageBackground, ScrollView, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";

import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdInput } from "src/components/forms/input";
import { PdLink } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { copyLinkToClipboard } from "src/util/functions";
import { useAppDispatch } from "src/redux";
import { useReferCompaniesModal, useVendorSetupAccount } from "src/hooks/modals";
import { kBaseSiteUrl } from "src/util/constants";

import { MyInvitesCard, ReferBrandsCard, ReferRepsCard } from "./cards";
import { ReferBrandsFaq, ReferSalesRepsFaq } from "./faqs";
import { tVendorData } from "tmt_api/src/types/types.validator";
import { getDefaultAddress } from "src/util/user";

const BANNER_HEIGHT = 440;

const P19_ReferralsRewards: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const vendorSetupAccount = useVendorSetupAccount();
  const referCompaniesModal = useReferCompaniesModal();
  const rRetailer = useSelector((state) => (state.CompanyReducer.type === "retailer" ? state.CompanyReducer : null));
  const isSetupForRewards = !!rRetailer?.vendorId;

  const [view, setView] = React.useState<"brands" | "reps">("brands");
  const rReferralLink = useSelector(
    (state) => `${kBaseSiteUrl}/r?uuid=${state.UserReducer.profile.companyRefPath.split("/")?.[1] ?? ""}`,
  );
  const [emailsList, setEmailsList] = React.useState("");

  const handleOnPressInvite = async () => {
    if (!emailsList) return;

    await referCompaniesModal.openAsync({
      type: view === "brands" ? "brand" : "rep",
      defaultEmailsList: emailsList,
    });

    setEmailsList("");
  };

  const handleOnPressCopyLink = () => {
    if (rReferralLink) {
      copyLinkToClipboard(rReferralLink);
      dispatch({ type: "RIBBON", message: "Link copied" });
    }
  };

  const handleOnPressConnect = () => {
    const address = getDefaultAddress(rRetailer?.addresses ?? []);
    const defaultValue: tVendorData = {
      businessName: rRetailer?.name ?? "",
      businessDomain: rRetailer?.website ?? "",
      ein: rRetailer?.taxId ?? "",
      url: rRetailer?.website ?? "",
      emailAddress: rRetailer?.email ?? "",
      address1: address.address1,
      address2: address.address2 ?? "",
      city: address.city,
      state: address.state_or_province,
      zipCode: address.postal_code,
      phone: rRetailer?.phone ?? "",
      country: address.country_code,
      accountNumber: "",
      routingNumber: "",
    };
    vendorSetupAccount.openAsync({ mode: "retailer", defaultValue, isEditing: isSetupForRewards });
  };

  return (
    <ScrollView>
      <ImageBackground style={styles.pageHeaderImage} source={{ uri: "/images/rewards-banner.png" }} resizeMode="cover">
        <PdStack
          direction={isMobile ? "vertical" : "horizontal"}
          layout={[2, 1]}
          gap={isMobile ? 10 : 30}
          style={[
            styles.flex1,
            styles.bgBrandDarkLight,
            isMobile ? [styles.px6, styles.py8] : [styles.px20, styles.py16],
          ]}
        >
          <View>
            <PdStack direction={isMobile ? "vertical" : "horizontal"} style={styles.mb4}>
              <PdButton
                size="sm"
                variant={view === "brands" ? "solid" : "ghost"}
                colorScheme="white"
                onPress={() => setView("brands")}
              >
                <PdButtonLabel>Refer Brands</PdButtonLabel>
              </PdButton>
              {/* <PdButton
                size="sm"
                variant={view === "reps" ? "solid" : "ghost"}
                colorScheme="white"
                onPress={() => setView("reps")}
              >
                <PdButtonLabel>Refer Sales Reps</PdButtonLabel>
              </PdButton> */}
            </PdStack>
            <PdText variant="h3" style={[styles.mb4, styles.textWhite]}>
              {view === "brands" ? "Refer a brand, earn up to $2,000!" : "Refer a sales rep, get $100!"}
            </PdText>
            <PdText style={styles.textWhite}>
              {view === "brands"
                ? "Refer your brands to join The Main Tab wholesale and earn cash back rewards!"
                : "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore."}
            </PdText>
          </View>
          {view === "brands" ? <ReferBrandsCard /> : <ReferRepsCard />}
        </PdStack>
      </ImageBackground>
      <View
        style={[
          styles.flex1,
          styles.absolute,
          styles.mxAuto,
          isMobile ? styles.px6 : styles.px20,
          { top: BANNER_HEIGHT - (isMobile ? -180 : 60) },
        ]}
      >
        <View
          style={[
            styles.alignCenter,
            styles.bgGray,
            styles.mb4,
            isMobile ? [styles.px4, styles.py3] : [styles.px20, styles.py8],
          ]}
        >
          <PdText variant="subheader" style={styles.mb4}>
            Refer Now
          </PdText>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.wFull, styles.mb4]}>
            <PdInput
              value={emailsList}
              onChangeText={setEmailsList}
              style={[styles.wFull, styles.bgWhite]}
              placeholder="jane@email.com, john@email.com"
            />
            <PdButton onPress={handleOnPressInvite} variant="outline">
              <PdButtonLabel>Send Invite</PdButtonLabel>
            </PdButton>
          </PdStack>
          <PdText style={[styles.fontSm, styles.leadingSm, styles.textCenter]}>
            {view === "brands"
              ? "You can invite up to 100 brands at a time! Once you've entered the email addresses, click 'Send Invite'. You will have a chance to edit your message before sending. Invitation does not guarantee brand acceptance; approval of brand is at our discretion."
              : "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."}
          </PdText>
        </View>
        {view === "brands" ? (
          <>
            <PdStack direction={isMobile ? "vertical" : "horizontal"} gap={4} layout={[1, 1]} style={styles.mb4}>
              <View style={[styles.px6, styles.py8, styles.grayLight]}>
                <PdText variant="subheader" style={styles.mb4}>
                  Connect your bank account
                </PdText>
                <PdText style={styles.mb4}>Receive your cash back rewards directly to your bank account!</PdText>
                <PdButton onPress={handleOnPressConnect} style={styles.mtAuto}>
                  <PdButtonLabel>{isSetupForRewards ? "Edit" : "Connect"}</PdButtonLabel>
                </PdButton>
              </View>
              <View style={[styles.px6, styles.py8, styles.grayLight]}>
                <PdText variant="subheader" style={styles.mb4}>
                  Referral link
                </PdText>
                <PdText style={styles.mb4}>
                  Earn up to $2,000 in credit when a brand signs up for The Main Tab using your link.
                </PdText>
                <PdButton
                  onPress={handleOnPressCopyLink}
                  style={styles.mtAuto}
                  rightIcon={<PdLink style={styles.hSm} />}
                >
                  <PdButtonLabel>Copy Link</PdButtonLabel>
                </PdButton>
              </View>
            </PdStack>
            <MyInvitesCard style={styles.mb8} type="brand" />
            <ReferBrandsFaq style={styles.mb8} />
          </>
        ) : (
          <>
            <PdStack direction={isMobile ? "vertical" : "horizontal"} gap={4} layout={[2, 1]} style={styles.mb4}>
              <MyInvitesCard type="rep" />
              <View style={isMobile ? styles.py8 : [styles.px6, styles.py8]}>
                <PdText variant="subheader" style={styles.mb4}>
                  Referral link
                </PdText>
                <PdText style={styles.mb4}>
                  Earn up to $2,000 in credit when a brand signs up for The Main Tab using your link.
                </PdText>
                <PdButton
                  onPress={handleOnPressCopyLink}
                  style={styles.mtAuto}
                  rightIcon={<PdLink style={styles.hSm} />}
                >
                  <PdButtonLabel>Copy Link</PdButtonLabel>
                </PdButton>
              </View>
            </PdStack>
            <ReferSalesRepsFaq style={styles.mb8} />
          </>
        )}
      </View>
    </ScrollView>
  );
};

export const P19 = React.memo(P19_ReferralsRewards);

const styles = StyleSheet.create({
  absolute: {
    position: "absolute",
  },
  alignCenter: {
    alignItems: "center",
  },
  bgBrandDarkLight: {
    backgroundColor: kColors.brandDark100,
  },
  bgGray: {
    backgroundColor: kColors.grayPink,
  },
  bgWhite: {
    backgroundColor: kColors.white,
  },
  flex1: {
    flex: 1,
  },
  fontSm: {
    fontSize: 10,
  },
  grayLight: {
    backgroundColor: kColors.gray50,
  },
  hSm: {
    height: 16,
  },
  leadingSm: {
    lineHeight: 16,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mb8: {
    marginBottom: kStyleConst.kSpacing * 8,
  },
  mtAuto: {
    marginTop: "auto",
  },
  mxAuto: {
    left: 0,
    right: 0,
  },
  pageHeaderImage: {
    backgroundColor: kColors.gray,
    height: BANNER_HEIGHT,
  },
  px20: {
    paddingHorizontal: kStyleConst.kSpacing * 20,
  },
  px4: {
    paddingHorizontal: kStyleConst.kSpacing * 4,
  },
  px6: {
    paddingHorizontal: kStyleConst.kSpacing * 6,
  },
  py16: {
    paddingVertical: kStyleConst.kSpacing * 16,
  },
  py3: {
    paddingVertical: kStyleConst.kSpacing * 3,
  },
  py8: {
    paddingVertical: kStyleConst.kSpacing * 8,
  },
  textCenter: {
    textAlign: "center",
  },
  textWhite: {
    color: kColors.white,
  },
  wFull: {
    width: "100%",
  },
});
